import apiClient from './apiClient';

export interface WBSElement {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: 'PROJECT' | 'PHASE' | 'DELIVERABLE' | 'WORK_PACKAGE' | 'MILESTONE';
  level: number;
  parentId?: string;
  projectId: string;
  status: 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

  // Scheduling
  startDate?: string;
  endDate?: string;
  duration?: number;

  // Progress
  progress: number;
  progressMethod: 'AUTO' | 'MANUAL' | 'WEIGHTED';

  // Financial
  budget: number;
  actualCost: number;
  earnedValue: number;

  // EVM Metrics
  cpi?: number;
  spi?: number;
  eac?: number;
  etc?: number;
  vac?: number;
  performanceStatus?: 'ON_TRACK' | 'BEHIND_SCHEDULE' | 'OVER_BUDGET' | 'BEHIND_AND_OVER';

  // Assignments
  assignedTeamId?: string;
  assignedToId?: string;

  // Children (for tree structure)
  children?: WBSElement[];

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface WBSTreeNode extends WBSElement {
  children: WBSTreeNode[];
  taskCount?: number;
  isExpanded?: boolean;
}

export interface EVMMetrics {
  pv: number; // Planned Value
  ev: number; // Earned Value
  ac: number; // Actual Cost
  cpi: number; // Cost Performance Index
  spi: number; // Schedule Performance Index
  eac: number; // Estimate at Completion
  etc: number; // Estimate to Complete
  vac: number; // Variance at Completion
  performanceStatus: 'ON_TRACK' | 'BEHIND_SCHEDULE' | 'OVER_BUDGET' | 'BEHIND_AND_OVER';
}

export interface CreateWBSElementDto {
  code?: string;
  name: string;
  description?: string;
  type: WBSElement['type'];
  parentId?: string;
  projectId: string;
  status?: WBSElement['status'];
  priority?: WBSElement['priority'];
  startDate?: string;
  endDate?: string;
  budget?: number;
  assignedTeamId?: string;
  assignedToId?: string;
}

export interface UpdateWBSElementDto extends Partial<CreateWBSElementDto> {
  progress?: number;
  progressMethod?: WBSElement['progressMethod'];
}

const normalizeType = (value: string): WBSElement['type'] => {
  if (value === 'WORKPACKAGE') return 'WORK_PACKAGE';
  if (value === 'ACTIVITY') return 'WORK_PACKAGE';
  if (value === 'PHASE') return 'PHASE';
  if (value === 'MILESTONE') return 'MILESTONE';
  return 'DELIVERABLE';
};

const normalizeWbsElement = (item: any): WBSElement => ({
  id: String(item.id),
  code: item.code || item.wbsCode,
  name: item.name || item.wbsName,
  description: item.description || '',
  type: normalizeType(item.type || item.wbsType),
  level: Number(item.level || 0),
  parentId: item.parentId ? String(item.parentId) : undefined,
  projectId: item.projectId,
  status: item.status || (item.deletedAt ? 'CANCELLED' : 'ACTIVE'),
  priority: item.priority || 'MEDIUM',
  startDate: item.startDate || item.plannedStartDate,
  endDate: item.endDate || item.plannedEndDate,
  duration: item.duration || item.durationDays,
  progress: Number(item.progress ?? item.progressPercent ?? 0),
  progressMethod: item.progressMethod || 'AUTO',
  budget: Number(item.budget ?? item.budgetAmount ?? 0),
  actualCost: Number(item.actualCost || 0),
  earnedValue: Number(item.earnedValue || 0),
  assignedTeamId: item.assignedTeamId || item.responsibleTeamId,
  assignedToId: item.assignedToId || item.responsibleUserId,
  children: item.children?.map(normalizeWbsTreeNode),
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
});

const normalizeWbsTreeNode = (item: any): WBSTreeNode => ({
  ...normalizeWbsElement(item),
  children: (item.children || []).map(normalizeWbsTreeNode),
  taskCount: item.taskCount,
  isExpanded: item.isExpanded,
});

class WBSService {
  /**
   * Get full WBS tree for a project
   */
  async getProjectTree(projectId: string): Promise<WBSTreeNode[]> {
    const response = await apiClient.get(`/wbs/projects/${projectId}/tree`);
    return response.data.map(normalizeWbsTreeNode);
  }

  /**
   * Get a single WBS element by ID
   */
  async getById(id: string): Promise<WBSElement> {
    const response = await apiClient.get(`/wbs/${id}`);
    return normalizeWbsElement(response.data);
  }

  /**
   * Create a new WBS element
   */
  async create(data: CreateWBSElementDto): Promise<WBSElement> {
    const response = await apiClient.post('/wbs', data);
    return response.data;
  }

  /**
   * Update a WBS element
   */
  async update(id: string, data: UpdateWBSElementDto): Promise<WBSElement> {
    const response = await apiClient.put(`/wbs/${id}`, data);
    return response.data;
  }

  /**
   * Delete a WBS element
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/wbs/${id}`);
  }

  /**
   * Get ancestors (path to root) for a WBS element
   */
  async getAncestors(id: string): Promise<WBSElement[]> {
    const response = await apiClient.get(`/wbs/${id}/ancestors`);
    return response.data;
  }

  /**
   * Get descendants (subtree) for a WBS element
   */
  async getDescendants(id: string): Promise<WBSElement[]> {
    const response = await apiClient.get(`/wbs/${id}/descendants`);
    return response.data;
  }

  /**
   * Get EVM metrics for a WBS element
   */
  async getEVMMetrics(id: string): Promise<EVMMetrics> {
    const response = await apiClient.get(`/wbs/${id}/evm`);
    return response.data;
  }

  /**
   * Update progress for a WBS element
   */
  async updateProgress(id: string, progress: number): Promise<WBSElement> {
    const response = await apiClient.put(`/wbs/${id}/progress`, { progress });
    return response.data;
  }

  /**
   * Move a WBS element to a new parent
   */
  async move(id: string, newParentId: string | null): Promise<WBSElement> {
    const response = await apiClient.post(`/wbs/${id}/move`, { newParentId });
    return response.data;
  }

  /**
   * Get tasks linked to a WBS element
   */
  async getLinkedTasks(id: string): Promise<any[]> {
    const response = await apiClient.get(`/wbs/${id}/tasks`);
    return response.data;
  }

  /**
   * Get WBS summary statistics for a project
   */
  async getProjectSummary(projectId: string): Promise<any> {
    const response = await apiClient.get(`/wbs/projects/${projectId}/summary`);
    return response.data;
  }
}

export default new WBSService();
